/**
 * FikoVideoRecord Dialog Component
 * 3D Viewport video kayıt - Ayrı pencere
 * Kaynak seçimi: Canvas (sadece 3D) veya Tam Ekran (imleç + dialoglar)
 *
 * @author FikoYT
 * @version 1.1.0
 */

import { Component, Input, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoScreenshot, VideoOptions, VideoResult } from './FikoScreenshot';

@Component({
  selector: 'fikoyt-video-record',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="vr-panel">
      <!-- Kayıt Durumu -->
      <div class="vr-status" [class.recording]="isRecording">
        <div class="vr-indicator" *ngIf="isRecording"></div>
        <span class="vr-time">{{ formatDuration(recordingDuration) }}</span>
        <span class="vr-label" *ngIf="!isRecording && !lastVideoResult">Hazır</span>
        <span class="vr-label" *ngIf="lastVideoResult && !isRecording">Video Hazır</span>
      </div>

      <!-- Ayarlar (kayıt yokken) -->
      <div class="vr-settings" *ngIf="!isRecording">
        <!-- Kaynak Seçimi -->
        <div class="vr-row">
          <label>Kaynak</label>
          <select [(ngModel)]="videoOptions.source">
            <option value="canvas">Sadece 3D</option>
            <option value="viewport">3D + Dialoglar</option>
            <option value="screen">Tam Ekran</option>
          </select>
        </div>

        <!-- Kaynak açıklamaları -->
        <div class="vr-hint" *ngIf="videoOptions.source === 'viewport'">
          3D sahne + açık dialoglar + imleç
        </div>
        <div class="vr-hint screen" *ngIf="videoOptions.source === 'screen'">
          Tüm ekran (izin gerektirir)
        </div>

        <div class="vr-row">
          <label>Kalite</label>
          <select [(ngModel)]="videoOptions.quality">
            <option value="low">Düşük (2.5 Mbps)</option>
            <option value="medium">Orta (5 Mbps)</option>
            <option value="high">Yüksek (10 Mbps)</option>
          </select>
        </div>

        <div class="vr-row">
          <label>FPS</label>
          <select [(ngModel)]="videoOptions.fps">
            <option [ngValue]="30">30 FPS</option>
            <option [ngValue]="60">60 FPS</option>
          </select>
        </div>
      </div>

      <!-- Ana Buton -->
      <button class="vr-main-btn"
              [class.recording]="isRecording"
              (click)="toggleRecording()">
        <span *ngIf="!isRecording">🎥 Kayıt Başlat</span>
        <span *ngIf="isRecording">⏹️ Kaydı Durdur</span>
      </button>

      <!-- İndirme Butonu -->
      <button class="vr-download-btn"
              *ngIf="lastVideoResult"
              (click)="downloadVideo()">
        📥 Video İndir ({{ formatDuration(lastVideoResult.duration) }})
      </button>

      <!-- Önizleme (kayıt sonrası) -->
      <div class="vr-preview" *ngIf="lastVideoResult">
        <video [src]="lastVideoResult.url" controls></video>
        <div class="vr-info">
          {{ lastVideoResult.width }}x{{ lastVideoResult.height }} |
          {{ formatFileSize(lastVideoResult.blob.size) }}
        </div>
      </div>
    </div>
  `,
  styles: [`
    .vr-panel {
      padding: 10px;
      min-width: 220px;
      max-width: 260px;
      font-size: 11px;
      background: linear-gradient(180deg, rgba(30,10,40,0.95) 0%, rgba(20,5,30,0.98) 100%);
      border-radius: 4px;
    }

    .vr-status {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 12px;
      background: rgba(0,0,0,0.4);
      border: 1px solid rgba(100,50,150,0.3);
      border-radius: 6px;
      margin-bottom: 10px;
    }

    .vr-status.recording {
      background: rgba(255,0,0,0.15);
      border-color: rgba(255,100,100,0.5);
    }

    .vr-indicator {
      width: 12px;
      height: 12px;
      background: #ff4444;
      border-radius: 50%;
      animation: recBlink 1s ease-in-out infinite;
    }

    @keyframes recBlink {
      0%, 100% { opacity: 1; box-shadow: 0 0 8px #ff4444; }
      50% { opacity: 0.4; box-shadow: 0 0 2px #ff4444; }
    }

    .vr-time {
      font-family: 'Courier New', monospace;
      font-size: 20px;
      font-weight: bold;
      color: #ff6666;
      letter-spacing: 2px;
    }

    .vr-status:not(.recording) .vr-time {
      color: rgba(150,100,200,0.8);
    }

    .vr-label {
      font-size: 10px;
      color: rgba(150,100,200,0.7);
      text-transform: uppercase;
    }

    .vr-settings {
      margin-bottom: 10px;
    }

    .vr-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 8px;
      background: rgba(0,0,0,0.3);
      border-radius: 4px;
      border: 1px solid rgba(100,50,150,0.2);
      margin-bottom: 6px;
    }

    .vr-row label {
      color: rgba(180,140,220,0.9);
      font-size: 10px;
      font-weight: 500;
    }

    .vr-row select {
      background: rgba(50,20,70,0.8);
      border: 1px solid rgba(150,100,200,0.4);
      color: #fff;
      padding: 3px 6px;
      border-radius: 3px;
      font-size: 10px;
      outline: none;
      cursor: pointer;
    }

    .vr-row select:focus {
      border-color: rgba(180,140,220,0.8);
    }

    .vr-hint {
      font-size: 9px;
      color: rgba(100,200,150,0.8);
      text-align: center;
      padding: 4px 8px;
      margin-bottom: 6px;
      background: rgba(0,100,80,0.2);
      border-radius: 3px;
      border: 1px solid rgba(0,150,100,0.3);
    }

    .vr-hint.screen {
      color: rgba(200,150,100,0.8);
      background: rgba(100,60,0,0.2);
      border-color: rgba(150,100,0,0.3);
    }

    .vr-main-btn {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid rgba(100,50,150,0.5);
      background: linear-gradient(180deg, rgba(80,40,120,0.8) 0%, rgba(50,20,80,0.9) 100%);
      color: #fff;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      margin-bottom: 6px;
    }

    .vr-main-btn:hover {
      background: linear-gradient(180deg, rgba(100,60,150,0.9) 0%, rgba(70,40,110,1) 100%);
      border-color: rgba(150,100,200,0.8);
    }

    .vr-main-btn.recording {
      background: linear-gradient(180deg, rgba(200,50,50,0.9) 0%, rgba(150,30,30,0.95) 100%);
      border-color: rgba(255,100,100,0.7);
      animation: recPulse 1.5s ease-in-out infinite;
    }

    @keyframes recPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255,0,0,0.4); }
      50% { box-shadow: 0 0 12px 3px rgba(255,0,0,0.5); }
    }

    .vr-download-btn {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid rgba(0,200,150,0.5);
      background: linear-gradient(180deg, rgba(0,120,90,0.8) 0%, rgba(0,80,60,0.9) 100%);
      color: #fff;
      border-radius: 5px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      margin-bottom: 8px;
    }

    .vr-download-btn:hover {
      background: linear-gradient(180deg, rgba(0,150,110,0.9) 0%, rgba(0,100,75,1) 100%);
      border-color: rgba(0,255,180,0.7);
    }

    .vr-preview {
      background: rgba(0,0,0,0.5);
      border: 1px solid rgba(100,50,150,0.3);
      border-radius: 4px;
      padding: 6px;
    }

    .vr-preview video {
      width: 100%;
      border-radius: 3px;
      max-height: 120px;
    }

    .vr-info {
      text-align: center;
      font-size: 9px;
      color: rgba(150,100,200,0.7);
      margin-top: 4px;
    }
  `]
})
export class FikoVideoRecordComponent implements OnDestroy {
  // ========== DIALOG PATTERN INPUT'LARI ==========

  private _anaGosterim: any = null;

  @Input()
  public get anaGosterim(): any {
    return this._anaGosterim;
  }
  public set anaGosterim(v: any) {
    this._anaGosterim = v;
    if (v) {
      this.initService();
    }
  }

  private _pencere: any;
  private dialogCloseSub: Subscription | null = null;

  @Input()
  public get pencere(): any {
    return this._pencere;
  }
  public set pencere(v: any) {
    this._pencere = v?.instance || v;

    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
      this.dialogCloseSub = null;
    }

    if (this._pencere && this._pencere.onDialogClose) {
      this.dialogCloseSub = this._pencere.onDialogClose.subscribe(() => {
        setTimeout(() => this.temizle(), 0);
      });
    }

    this.initComponent();
  }

  @Input() ayarlar: any = {};
  @Input() parametreler: any = {};

  // ========== COMPONENT STATE ==========

  isRecording = false;
  recordingDuration = 0;
  lastVideoResult: VideoResult | null = null;
  videoOptions: VideoOptions = {
    format: 'webm',
    fps: 30,
    quality: 'medium',
    maxDuration: 0,
    source: 'canvas',
    showCursor: false
  };

  private recordingTimer: any = null;
  private screenshotService: FikoScreenshot | null = null;

  constructor(private cd: ChangeDetectorRef, private ngZone: NgZone) {}

  ngOnDestroy(): void {
    this.temizle();
  }

  private initComponent(): void {
    if (this.parametreler?.anaGosterim) {
      this._anaGosterim = this.parametreler.anaGosterim;
    }
    if (this._anaGosterim) {
      this.initService();
    }
  }

  private initService(): void {
    if (!this._anaGosterim) return;

    const renderer = this._anaGosterim.sunucu;
    const scene = this._anaGosterim.sahneler?.[0];
    const camera = this._anaGosterim.aktifKamera;

    if (renderer && scene && camera) {
      this.screenshotService = new FikoScreenshot(renderer, scene, camera);
    }
  }

  private temizle(): void {
    if (this.screenshotService) {
      this.screenshotService.dispose();
      this.screenshotService = null;
    }

    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }

    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
      this.dialogCloseSub = null;
    }

    if (this.lastVideoResult?.url) {
      URL.revokeObjectURL(this.lastVideoResult.url);
      this.lastVideoResult = null;
    }
  }

  private showToast(mesaj: string, tip: 'success' | 'error' | 'warning' = 'success'): void {
    const toast = document.createElement('div');
    toast.textContent = mesaj;
    toast.style.cssText = `
      position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
      padding: 8px 16px; border-radius: 4px; color: white; font-size: 12px;
      z-index: 99999; animation: toastFade 1.5s ease-in-out forwards;
      background: ${tip === 'success' ? '#9c27b0' : tip === 'error' ? '#ff4d4f' : '#faad14'};
      border: 1px solid rgba(255,255,255,0.2); box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    if (!document.getElementById('toast-style')) {
      const style = document.createElement('style');
      style.id = 'toast-style';
      style.textContent = `@keyframes toastFade { 0%,15%,85% { opacity: 1; } 100% { opacity: 0; } }`;
      document.head.appendChild(style);
    }
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 1500);
  }

  // ========== VIDEO KAYIT METODLARI ==========

  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  async startRecording(): Promise<void> {
    if (!this.screenshotService) this.initService();
    if (!this.screenshotService) {
      this.showToast('Servis hazır değil', 'error');
      return;
    }

    // Önceki videoyu temizle
    if (this.lastVideoResult?.url) {
      URL.revokeObjectURL(this.lastVideoResult.url);
      this.lastVideoResult = null;
    }

    let started = false;

    // Kaynak moduna göre başlat
    if (this.videoOptions.source === 'screen') {
      // Ekran kaydı (async - izin gerektirir)
      this.showToast('Ekran paylaşımı seçin...', 'warning');
      this.cd.detectChanges();
      started = await this.screenshotService.startScreenRecording(this.videoOptions);
    } else if (this.videoOptions.source === 'viewport') {
      // Viewport kaydı (3D + dialoglar composite)
      started = this.screenshotService.startViewportRecording(this.videoOptions);
    } else {
      // Canvas kaydı (senkron - sadece 3D)
      started = this.screenshotService.startRecording(this.videoOptions);
    }

    if (started) {
      this.isRecording = true;
      this.recordingDuration = 0;

      // Süre sayacı başlat
      this.ngZone.runOutsideAngular(() => {
        this.recordingTimer = setInterval(() => {
          this.ngZone.run(() => {
            this.recordingDuration = this.screenshotService?.getRecordingStatus().duration ?? 0;
            this.cd.detectChanges();
          });
        }, 100);
      });

      const modeText = this.videoOptions.source === 'screen' ? 'Ekran kaydı' :
                       this.videoOptions.source === 'viewport' ? 'Viewport kaydı' : 'Kayıt';
      this.showToast(`${modeText} başladı`, 'success');
      this.cd.detectChanges();
    } else {
      this.showToast('Kayıt başlatılamadı', 'error');
    }
  }

  async stopRecording(): Promise<void> {
    if (!this.screenshotService || !this.isRecording) return;

    // Timer durdur
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }

    this.showToast('Kayıt durduruluyor...', 'success');

    const result = await this.screenshotService.stopRecording();
    this.isRecording = false;

    if (result) {
      this.lastVideoResult = result;
      this.showToast(`Video hazır (${this.formatDuration(result.duration)})`, 'success');
    } else {
      this.showToast('Video kaydedilemedi', 'error');
    }

    this.cd.detectChanges();
  }

  async downloadVideo(): Promise<void> {
    if (!this.screenshotService || !this.lastVideoResult) return;

    try {
      await this.screenshotService.downloadVideo(this.lastVideoResult);
      this.showToast('Video indirildi', 'success');
    } catch (e) {
      this.showToast('İndirme hatası', 'error');
    }
  }

  formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms}`;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
